import React, { useState, useEffect, useMemo, useCallback } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getUomTypeHistory } from "../../../common/services/markets/uomTypeService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const UomTypeHistory = ({ uomTypeId }) => {
  const { t } = useTranslation(["markets"]);
  const [uomTypeHistory, setUomTypeHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [paginationState, setPaginationState] = useState({
    currentPage: 0,
    pageSize: 10,
  });

  // Fetch UOM type history on component mount
  useEffect(() => {
    const fetchHistory = async () => {
      const data = await getUomTypeHistory(uomTypeId);
      const formattedData = mapToViewModel(data);
      setUomTypeHistory(_.orderBy(formattedData, "id", "desc"));
      setIsLoading(false);
    };
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uomTypeId]);

  // Map API data to view model with formatted dates
  const mapToViewModel = useCallback((uomTypes) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };
    return uomTypes.map((uomType) => ({
      id: uomType.id,
      name: uomType.name,
      active: uomType.active,
      updatedByUserAccountId: uomType.createdByUserAccountId,
      updatedBy:
        uomType.updatedByUserAccountFirstName +
        " " +
        uomType.updatedByUserAccountLastName,
      updatedDate: new Date(uomType.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      writeDate: new Date(uomType.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: uomType.eventId,
    }));
  }, []);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "id",
        headerName: t("RecordID"),
        sortable: true,
        filter: true,
        sort: "desc",
      },
      {
        field: "name",
        headerName: t("UomType"),
        sortable: true,
        filter: true,
      },
      {
        field: "active",
        headerName: t("Active"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      {
        field: "updatedBy",
        headerName: t("UpdatedBy"),
        sortable: true,
        filter: true,
      },
      {
        field: "updatedDate",
        headerName: t("UpdateDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "writeDate",
        headerName: t("WriteDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "eventId",
        headerName: t("EventID"),
        sortable: true,
        filter: true,
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      flex: 1,
    }),
    []
  );

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      enabled: true,
      pageSize: paginationState?.pageSize || 10,
      pageSizeOptions: [10, 20, 50, 100],
    }),
    [paginationState?.pageSize]
  );

  // Handle pagination changes
  const handlePaginationChanged = useCallback((params) => {
    if (params && params.api) {
      setPaginationState({
        currentPage: params.api.paginationGetCurrentPage(),
        pageSize: params.api.paginationGetPageSize(),
      });
    }
  }, []);

  // Grid ready handler
  const onGridReady = useCallback(
    (params) => {
      if (paginationState && paginationState.currentPage >= 0) {
        setTimeout(() => {
          params.api.paginationGoToPage(paginationState.currentPage);
        }, 0);
      }
    },
    [paginationState]
  );

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container">
      <div className="mb-3">
        {t("UomTypeID")} {uomTypeId}
      </div>
      <ThemeableAgGrid
        className="table-extra-small"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={uomTypeHistory}
        getRowId={(params) => params.data.id.toString()}
        domLayout="autoHeight"
        animateRows={true}
        onGridReady={onGridReady}
        rowHeight={40}
        pagination={true}
        paginationPageSize={paginationConfig.pageSize}
        paginationPageSizeSelector={paginationConfig.pageSizeOptions}
        suppressPaginationPanel={false}
        onPaginationChanged={handlePaginationChanged}
      />
    </div>
  );
};

export default UomTypeHistory;

